import React from 'react'

import { Table } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimesheetEditTimesheetRequestFragment,
  TimesheetEstimatedTimeFragment,
  TimesheetPageEmployeesSettingFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import last from 'lodash/last'
import some from 'lodash/some'
import type moment from 'moment'

import { observer } from 'mobx-react'

import type Store from 'modules/attendance/components/ClockIn/Store'
import { AttendanceShiftLocationNodes } from 'modules/attendance/hooks/shift_locations/useFetchShiftLocations'
import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import { Inconsistencies } from 'modules/attendance/hooks/useShiftInconsistencies'
import { useAttendancePolicies } from 'modules/attendance/lib/attendancePolicies'
import { useContractsVersion } from 'modules/attendance/lib/contractsVersionContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import EditTimesheetRequestUtils from 'modules/attendance/lib/utils/EditTimesheetRequest'
import { ValidationError } from 'modules/attendance/lib/validateShiftBreakDurationRules'
import { IUseGetEmployeeProjectsTrackingMonthReturn } from 'modules/project_management/hooks/useGetEmployeeProjectsTrackingByMonth'

import PendingCreateRequestRow from './PendingCreateRequestRow'
import ShiftRow from './ShiftRow'
import AddShiftButton from './ShiftRow/AddShiftButton'

interface Props {
  store: Store
  colSpan?: number
  date: moment.Moment
  hasProjects?: boolean
  overtimeRequestMinutes: number
  allBreaks: BreakConfigurationFragment[]
  dayBreaks: AttendanceBreakConfigurations[]
  restTimeInconsistencies?: ValidationError[]
  shiftLocations?: AttendanceShiftLocationNodes
  attendanceSetting: TimesheetPageEmployeesSettingFragment
  background: 'champagne100' | 'grey100' | 'grey000White'
  editTimesheetRequests: TimesheetEditTimesheetRequestFragment[]
  projectsData?: IUseGetEmployeeProjectsTrackingMonthReturn | null
  showStartsNextDayCheckbox: boolean
  estimatedTime: TimesheetEstimatedTimeFragment
  trackedMinutes: number
  shifts: TimesheetPageShiftFragment[]
  inconsistencies?: Inconsistencies
}

const ShiftPanelTableBody = ({
  date,
  store,
  dayBreaks,
  allBreaks,
  background,
  hasProjects,
  shiftLocations,
  projectsData,
  attendanceSetting,
  overtimeRequestMinutes,
  restTimeInconsistencies,
  colSpan,
  showStartsNextDayCheckbox,
  trackedMinutes,
  estimatedTime,
  editTimesheetRequests,
  shifts,
  inconsistencies,
}: Props) => {
  const sortedShifts = AttendanceShiftUtils.sortShiftsByDateAndClockIn(shifts)
  const { reviewsStats, attendancePeriods } = useTimesheetData()
  const lastOfDay = last(sortedShifts)
  const isOpenDay = some(sortedShifts, (shift) => AttendanceShiftUtils.isOpenShift(shift))
  const { startsOn } = useContractsVersion()
  const { data } = useAttendancePolicies()
  const canEditAttendance = !!data?.employee?._policies?.editAttendance?.can
  const year = date.year()
  const month = date.month() + 1
  const currentPeriod = attendancePeriods?.filter((p) => p.month === month && p.year === year)
  const isReadonly = AttendanceShiftUtils.isReadOnly({
    date,
    approvedDates: reviewsStats?.approvedDates ?? [],
    startsOn,
    canEditAttendance,
    currentPeriod,
  })

  const showAddButton = !isOpenDay && !isReadonly
  const pendingShiftCreateRequestsForDay =
    EditTimesheetRequestUtils.getPendingCreateShiftRequests<TimesheetEditTimesheetRequestFragment>(
      editTimesheetRequests
    )

  return (
    <Table.Body>
      {sortedShifts.map((shift) => {
        const { id, periodId, timeSettingsBreakConfiguration } = shift
        const isLastOpenOfDay = lastOfDay?.id === id && AttendanceShiftUtils.isOpenShift(shift)

        const projectName = (hasProjects && (projectsData?.getProjectNameForShift(id) ?? '')) || ''
        const subprojectName =
          (hasProjects && (projectsData?.getSubprojectNameForShift(id) ?? '')) || ''

        const locationsByShift = shiftLocations?.filter(
          (location) => location.attendanceShiftId === id
        )
        const selectedBreak =
          allBreaks.find(
            (b: BreakConfigurationFragment) => b.id === timeSettingsBreakConfiguration?.id
          ) ?? null

        const selectedBreakConfig: AttendanceBreakConfigurations = {
          timeSettingsBreakConfigurationId: timeSettingsBreakConfiguration?.id,
          name: selectedBreak?.name ?? '',
          paid: selectedBreak?.paid ?? false,
        }

        return (
          <ShiftRow
            shift={shift}
            store={store}
            key={id + periodId}
            readonly={isReadonly}
            dayBreaks={dayBreaks}
            background={background}
            hasProjects={hasProjects}
            projectName={projectName}
            subprojectName={subprojectName}
            shiftLocations={locationsByShift}
            isLastOpenOfDay={isLastOpenOfDay}
            selectedBreak={selectedBreakConfig}
            attendanceSetting={attendanceSetting}
            overtimeRequestMinutes={overtimeRequestMinutes}
            restTimeInconsistencies={restTimeInconsistencies}
            estimatedTime={estimatedTime}
            trackedMinutes={trackedMinutes}
            editTimesheetRequests={editTimesheetRequests}
            shifts={shifts}
            inconsistencies={inconsistencies}
          />
        )
      })}

      {attendanceSetting?.editTimesheetAuthorization &&
        pendingShiftCreateRequestsForDay.map((request) => (
          <PendingCreateRequestRow key={request.id} background={background} request={request} />
        ))}

      {showAddButton && (
        <Table.Row background={background}>
          <Table.Cell wide noBorder colSpan={colSpan}>
            <AddShiftButton
              attendanceSetting={attendanceSetting}
              date={date}
              dayBreaks={dayBreaks}
              lastShift={lastOfDay}
              showStartsNextDayCheckbox={showStartsNextDayCheckbox}
              store={store}
            />
          </Table.Cell>
        </Table.Row>
      )}
    </Table.Body>
  )
}

export default observer(ShiftPanelTableBody)
